import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { setMaxApexAngleDegrees } from '../../store/reducers/workspace.reducer.ts';
import { selectActiveVersion } from '../../store/selectors/workspace.selector.ts';
import { isCalculationStarted } from '../../utils/jobStatus.ts';
import {
  DEFAULT_MAX_APEX_ANGLE_DEGREES,
  MAX_APEX_ANGLE_STEP_DEGREES,
  MAX_MAX_APEX_ANGLE_DEGREES,
  MIN_MAX_APEX_ANGLE_DEGREES,
  WIDE_APEX_ANGLE_DEGREES,
  clampMaxApexAngle,
  formatReachMultiple,
  groundReachMultiple,
} from '../../utils/maxApexAngle.ts';

const LIME = '#B3EC49';
const RED = '#f87171';

/**
 * Beam-angle control for the optimization step. The wedge is the cone from straight
 * down; past the horizon it climbs into the upper half of the room and turns red.
 */
export function MaxAngleControl() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const value = useAppSelector((s) => s.workspace.maxApexAngleDegrees);
  const starting = useAppSelector((s) => s.workspace.isOptimizationStarting);
  const started = isCalculationStarted(useAppSelector(selectActiveVersion));
  const disabled = starting || started;

  const wide = value > WIDE_APEX_ANGLE_DEGREES;
  const full = value >= MAX_MAX_APEX_ANGLE_DEGREES;
  const color = wide ? RED : LIME;
  const pct = (value / MAX_MAX_APEX_ANGLE_DEGREES) * 100;
  const widePct = (WIDE_APEX_ANGLE_DEGREES / MAX_MAX_APEX_ANGLE_DEGREES) * 100;
  const reach = groundReachMultiple(value);

  const status = full
    ? t('workspace.maxAngle.full')
    : value >= 90
      ? t('workspace.maxAngle.uplight')
      : t('workspace.maxAngle.reach', { multiple: formatReachMultiple(reach ?? 0) });

  const [text, setText] = useState(() => value.toFixed(1));
  const [editing, setEditing] = useState(false);
  const skipCommit = useRef(false);

  useEffect(() => {
    if (!editing) setText(value.toFixed(1));
  }, [editing, value]);

  const commit = (raw: string) => {
    const parsed = Number(raw.trim().replace(',', '.'));
    const next = clampMaxApexAngle(parsed);
    dispatch(setMaxApexAngleDegrees(next));
    setText(next.toFixed(1));
    setEditing(false);
  };

  return (
    <div className={`space-y-1.5 ${disabled ? 'opacity-60' : ''}`}>
      <div className="flex items-center justify-between gap-2">
        <label className="flex min-w-0 items-center gap-1.5 text-[12px] font-medium text-gray-200">
          <span className="truncate">{t('workspace.maxAngle.label')}</span>
          <input
            type="number"
            min={MIN_MAX_APEX_ANGLE_DEGREES}
            max={MAX_MAX_APEX_ANGLE_DEGREES}
            step={MAX_APEX_ANGLE_STEP_DEGREES}
            value={text}
            disabled={disabled}
            aria-valuemin={MIN_MAX_APEX_ANGLE_DEGREES}
            aria-valuemax={MAX_MAX_APEX_ANGLE_DEGREES}
            aria-valuenow={value}
            onFocus={(e) => {
              setEditing(true);
              setText(value.toFixed(1));
              e.target.select();
            }}
            onChange={(e) => setText(e.target.value)}
            onBlur={(e) => {
              if (skipCommit.current) {
                skipCommit.current = false;
                return;
              }
              commit(e.currentTarget.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') {
                skipCommit.current = true;
                setText(value.toFixed(1));
                setEditing(false);
                e.currentTarget.blur();
              }
            }}
            className="w-14 shrink-0 rounded bg-black/40 px-1 py-0.5 text-right text-[12px] tabular-nums [appearance:textfield] focus:outline-none focus:ring-1 focus:ring-lime-400 disabled:cursor-not-allowed [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            style={{ color }}
          />
          <span className="shrink-0" style={{ color }}>
            °
          </span>
        </label>
        <button
          type="button"
          disabled={disabled || value === DEFAULT_MAX_APEX_ANGLE_DEGREES}
          onClick={() => dispatch(setMaxApexAngleDegrees(DEFAULT_MAX_APEX_ANGLE_DEGREES))}
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors disabled:cursor-default ${
            value === DEFAULT_MAX_APEX_ANGLE_DEGREES
              ? 'bg-lime-400/15 text-lime-300'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
        >
          {t('workspace.maxAngle.standard')}
        </button>
      </div>

      <BeamWedge degrees={value} color={color} />

      <div className="relative h-5 flex items-center">
        <div className="absolute inset-x-0 h-1.5 rounded-full bg-[#2a2a2a] ring-1 ring-inset ring-white/5" />
        <div
          className="absolute h-1.5 rounded-r-full bg-red-500/25"
          style={{ left: `${widePct}%`, width: `${100 - widePct}%` }}
        />
        {wide ? (
          <>
            <div className="absolute left-0 h-1.5 bg-[#B3EC49]" style={{ width: `${widePct}%` }} />
            <div
              className="absolute h-1.5 rounded-r-full bg-red-400"
              style={{ left: `${widePct}%`, width: `${pct - widePct}%` }}
            />
          </>
        ) : (
          <div className="absolute left-0 h-1.5 rounded-full" style={{ width: `${pct}%`, backgroundColor: LIME }} />
        )}
        <div
          className="absolute top-1/2 h-3 w-px -translate-y-1/2 bg-white/40"
          style={{ left: `${widePct}%` }}
          title={t('workspace.maxAngle.wideMark')}
        />
        <div
          className="absolute h-3.5 w-3.5 -translate-x-1/2 rounded-full border-2 border-[#1b1b1b] shadow-md shadow-black/40 pointer-events-none"
          style={{ left: `${pct}%`, backgroundColor: color }}
        />
        <input
          type="range"
          min={MIN_MAX_APEX_ANGLE_DEGREES}
          max={MAX_MAX_APEX_ANGLE_DEGREES}
          step={MAX_APEX_ANGLE_STEP_DEGREES}
          value={value}
          disabled={disabled}
          aria-label={t('workspace.maxAngle.label')}
          aria-valuemin={MIN_MAX_APEX_ANGLE_DEGREES}
          aria-valuemax={MAX_MAX_APEX_ANGLE_DEGREES}
          aria-valuenow={value}
          aria-valuetext={t('workspace.maxAngle.value', { value: value.toFixed(1) })}
          onChange={(e) => dispatch(setMaxApexAngleDegrees(parseFloat(e.target.value)))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
        />
      </div>
      <div className="relative h-4 text-[10px] tabular-nums text-gray-500">
        <span className="absolute left-0 top-0">0°</span>
        <span
          className={`absolute top-0 -translate-x-1/2 ${wide ? 'text-red-300/80' : ''}`}
          style={{ left: `${widePct}%` }}
        >
          80°
        </span>
        <span className="absolute right-0 top-0">180°</span>
      </div>
      <p className={`min-h-8 text-[11px] leading-4 ${wide ? 'text-red-200' : 'text-gray-400'}`} role="status">
        {status}
      </p>
    </div>
  );
}

/** Lit sector around the lamp. 0° is straight down; the outline is the full sphere. */
function BeamWedge({ degrees, color }: { degrees: number; color: string }) {
  const cx = 60;
  const cy = 60;
  const radius = 46;
  const clamped = Math.min(180, Math.max(0, degrees));

  const at = (degFromNadir: number) => {
    const rad = (degFromNadir * Math.PI) / 180;
    return `${(cx + Math.sin(rad) * radius).toFixed(2)} ${(cy + Math.cos(rad) * radius).toFixed(2)}`;
  };

  let d: string;
  if (clamped >= 180) {
    d = `M ${cx} ${cy - radius} A ${radius} ${radius} 0 1 1 ${cx} ${cy + radius} A ${radius} ${radius} 0 1 1 ${cx} ${cy - radius} Z`;
  } else if (clamped <= 0) {
    d = `M ${cx} ${cy} L ${at(0)}`;
  } else {
    const steps = Math.max(16, Math.ceil((clamped * 2) / 4));
    const edge: string[] = [];
    for (let i = 0; i <= steps; i++) {
      edge.push(at(-clamped + (2 * clamped * i) / steps));
    }
    d = `M ${cx} ${cy} L ${edge.join(' L ')} Z`;
  }

  return (
    <svg viewBox="0 0 120 120" className="mx-auto block h-[96px] w-[96px]" aria-hidden>
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="1" />
      <line x1={cx - radius} y1={cy} x2={cx + radius} y2={cy} stroke="rgba(255,255,255,0.28)" strokeDasharray="2 3" />
      <path
        d={d}
        fill={color}
        fillOpacity={clamped >= 180 ? 0.35 : 0.5}
        stroke={color}
        strokeWidth="1.25"
        strokeLinejoin="round"
      />
      <circle cx={cx} cy={cy} r="2.5" fill="#1b1b1b" />
      <circle cx={cx} cy={cy} r="1.6" fill={color} />
    </svg>
  );
}
